import { shallowCallback, type StateCreatorEnhance } from '@halo/zustand-enhance';
import { uniq } from 'lodash';
import type { OriginalSession } from '@halo/chat-sdk';
import { DefaultCollapseGroupId, GroupMemberJoinMode, SessionOperate } from '@halo/chat-sdk';
import {
  isSessionCategory,
  SessionCategoryPrefix,
  StateEnum,
  type Session,
} from '@halo/chat-sdk';
import type { RequiredSome } from '@halo/utils';
import { deepMerge, doExpireAnimate, safeJsonParse, type DeepPartial } from '@halo/utils';
import type { SessionApi } from '@halo/chat-sdk/src/apis/session';
import type { AppStore } from '../main';
import { createSessionStore, type SessionStoreInstance } from './main';

export enum SessionsStatus {
  LOADING = 1,
  GETTING,
  READY,
}

export type CreateSessionsOptions = Partial<
  Pick<SessionsStore, 'messagesMaxLimit' | 'messagesLoadLimit'>
>;

export interface SessionsStore {
  /**
   * All sessions, including collapsed sessions
   */
  sessions: Session[];
  /**
   * Expanded sessions except collapsed sessions
   */
  sessionsExpanded: Session[];
  /**
   * Collapsed sessions
   */
  sessionsCollapsed: Session[];
  pinnedSessions: string[];
  sessionsStatus: SessionsStatus;
  activatedSessionId: string | undefined;
  sessionStores: Record<Session['chat_id'], SessionStoreInstance>;
  messagesMaxLimit: number;
  messagesLoadLimit: number;
  createSessions: (options: CreateSessionsOptions) => void;
  createSingleSession: (userId: string) => Promise<Session>;
  createGroupSession: (
    group_name: string,
    avatar: string,
    description: string,
    invite_member_list: string[],
    group_id_list: string[]
  ) => Promise<Session>;
  joinSession: (
    chat_id_like: string,
    join_mode?: GroupMemberJoinMode
  ) => ReturnType<SessionApi['joinSession']>;
  watchSessionsEnhanced: () => void;
  watchSessionsActivated: () => void;
  initSessionsWatches: () => () => void;
  initSessions: () => Promise<void>;
  activeSession: (session_id: Session['chat_id']) => void;
  destroySession: (session_id: Session['chat_id']) => void;
  removeSession: (session_id: Session['chat_id']) => void;
  insertSession: (session: Session) => void;
  updateSession: (session: RequiredSome<DeepPartial<Session>, 'chat_id'>) => void;
  getSession: (session_id: Session['chat_id']) => Session | undefined;
  hideSession: (session_id: Session['chat_id'], is_delete?: boolean) => Promise<void>;
  getCollapseSessionById: (collapse_group_id: string) => Promise<Session | undefined>;
  setSessionAtAllMuted: (chat_id: string, muted: boolean) => Promise<void>;
  setSessionMuted: (chat_id: string, muted: boolean) => Promise<void>;
  setSessionCollapse: (chat_id: string, collapse_group_id: string) => Promise<void>;
  setPinnedSessions: (pinnedSessions: string[]) => void;
  changePinnedList: (chat_id: string, isPinned: boolean, isCollapsed: boolean) => Promise<void>;
  getChatStatus: (options: Record<string, string>) => Promise<void>;
}

export const sessionsStoreCreator: StateCreatorEnhance<AppStore> = (set, get, instance) =>
  ({
    sessions: [],
    sessionsExpanded: [],
    sessionsCollapsed: [],
    sessionsStatus: SessionsStatus.LOADING,
    sessionStores: {},
    pinnedSessions: [],
    // TODO amo: Auto calculate
    createSessions: ({ messagesMaxLimit, messagesLoadLimit }) => {
      const { client, initSessionsWatches } = get();

      initSessionsWatches();

      function handleSessionsGetting() {
        const { sessionsStatus } = get();
        if (sessionsStatus !== SessionsStatus.READY) {
          set({ sessionsStatus: SessionsStatus.GETTING });
        }
      }

      client.addEventListener('FullSessionsSnapshot', ({ detail: { sessions } }) => {
        sessions.sort((a, b) => b.update_at - a.update_at);
        set({ sessions });
        handleSessionsGetting();
      });
      client.addEventListener('UpdateFullSession', ({ detail: { session } }) => {
        const { insertSession } = get();
        insertSession(session);
        handleSessionsGetting();
      });
      client.addEventListener('GroupNameChanged', ({ detail: { group_id, name } }) => {
        const { updateSession } = get();
        updateSession({
          chat_id: group_id,
          name,
        });
      });
      client.addEventListener(
        'SessionExpiredTimeUpdated',
        ({ detail: { chat_id, expire_duration } }) => {
          const { updateSession } = get();
          updateSession({
            chat_id,
            expire_duration,
          });
        }
      );
      // TODO amo: Merge to UpdatePartialSession
      client.addEventListener('UserStateChanged', ({ detail: { status, user_id } }) => {
        const { updateSession, sessions } = get();
        const session = sessions.find((s) => s.chat_id.split('_').includes(user_id));
        if (session?.user_info?.state !== status) {
          const name = 'Deleted Account';
          updateSession({
            chat_id: session?.chat_id,
            avatar: StateEnum.DELETE_ACCOUNT.toString(),
            name,
            user_info: {
              state: status,
              avatar: StateEnum.DELETE_ACCOUNT.toString(),
              name,
            },
          });
        }
      });
      client.addEventListener('UpdatePartialSession', ({ detail }) => {
        get().updateSession(detail);
      });
      client.addEventListener('UserProfileChanged', ({ detail }) => {
        const { update_profile } = detail;
        const { sessions, updateSession } = get();
        const session = sessions.find(
          (s) =>
            isSessionCategory(s.chat_id, SessionCategoryPrefix.Single) &&
            s.user_info?.user_id === detail.user_id
        );
        if (session) {
          const avatar = update_profile.get('avatar');
          const first_name = update_profile.get('first_name');
          const last_name = update_profile.get('last_name');
          const name = update_profile.get('name');
          const username = update_profile.get('username');
          updateSession({
            chat_id: session.chat_id,
            ...session,
            avatar: avatar ?? session.avatar,
            name: name ?? session.name,
            user_info: {
              ...session.user_info,
              first_name: first_name ?? session.user_info?.first_name,
              last_name: last_name ?? session.user_info?.last_name,
              name: name ?? session.user_info?.name,
              username: username ?? session.user_info?.username,
            },
          });
        }
      });
      client.addEventListener('GroupInfoChanged', ({ detail: { group_id, data } }) => {
        const { updateSession } = get();
        updateSession({
          chat_id: group_id,
          avatar: data.avatar,
          name: data.group_name,
        });
      });
      client.addEventListener('ContactInfoListChanged', ({ detail: { data } }) => {
        const { sessions, updateSession } = get();
        const map = sessions.reduce(
          (acc, session) => {
            const isSingle = isSessionCategory(session.chat_id, SessionCategoryPrefix.Single);
            if (isSingle && session.user_info) {
              acc[session.user_info.user_id] = session;
            }
            return acc;
          },
          {} as Record<string, Session>
        );

        data.forEach((user) => {
          const session = map[user.user_id];
          if (session) {
            updateSession({
              chat_id: session.chat_id,
              name: user.name,
              avatar: user.avatar,
              user_info: user,
            });
          }
        });
      });
      client.addEventListener(
        'SessionCollapseChanged',
        ({ detail: { chat_id, collapse_group_id } }) => {
          const { updateSession } = get();
          updateSession({
            chat_id,
            collapse_group_id,
          });
        }
      );
      client.addEventListener('SessionOperateChanged', ({ detail: { chat_id, operate } }) => {
        const { updateSession } = get();
        updateSession({
          chat_id,
          operate,
        });
      });
      client.addEventListener('SessionPinnedUpdated', ({ detail: { chat_ids } }) => {
        const { setPinnedSessions } = get();
        setPinnedSessions(chat_ids);
      });

      client.addEventListener('MediaCallActionChanged', ({ detail: { chat_id, action } }) => {
        const { updateSession } = get();
        if (chat_id) {
          updateSession({
            chat_id: chat_id,
            isOnACall: action == 1,
          });
        }
      });

      set({ messagesMaxLimit, messagesLoadLimit });
    },
    initSessionsWatches: () => {
      const { watchSessionsEnhanced, watchSessionsActivated } = get();
      instance.subscribe((store) => store.sessions, shallowCallback(watchSessionsEnhanced));
      instance.subscribe(
        (state) => [state.sessions.length, state.user?.user_id] as const,
        shallowCallback(watchSessionsActivated)
      );
    },
    watchSessionsEnhanced: () => {
      const { sessions: allSessions } = get();
      const sessionsExpanded: Session[] = [];
      const sessionsCollapsed: Session[] = [];
      let totalUnreadCountCollapsed = 0;

      let latestCollapsedSession: Session | undefined;
      let collapseSessionItem: Session | undefined;
      let collapseSessionItemInserted = false;

      allSessions.forEach((session) => {
        if (session.collapse_group_id) {
          // totalUnreadCountCollapsed += session.unread_count || 0;
          const isMarkedUnread = session.operateAccess?.[SessionOperate.MARKED_UNREAD];
          if (session.unread_count || isMarkedUnread) {
            totalUnreadCountCollapsed += 1;
          }
          sessionsCollapsed.push(session);
          if (!latestCollapsedSession) {
            latestCollapsedSession = session;
            collapseSessionItem = {
              ...session,
              collapse_group_id: DefaultCollapseGroupId,
              chat_id: DefaultCollapseGroupId,
              avatar: '',
              name: 'Collapsed Chats',
              last_message_groupname: session.last_message ? session.name : '',
              last_message_chat_id: session.chat_id,
            };
          }
          collapseSessionItem.unread_count = totalUnreadCountCollapsed;
        } else {
          if (
            collapseSessionItem &&
            !collapseSessionItemInserted &&
            session.update_at < collapseSessionItem.update_at
          ) {
            sessionsExpanded.push(collapseSessionItem);
            collapseSessionItemInserted = true;
          }
          sessionsExpanded.push(session);
        }
      });

      if (collapseSessionItem && !collapseSessionItemInserted) {
        sessionsExpanded.push(collapseSessionItem);
      }

      set({ sessionsExpanded, sessionsCollapsed });
    },
    watchSessionsActivated: () => {
      const { sessions, user } = get();
      if (sessions?.length && user?.user_id) {
        const { sessionStores, activatedSessionId, activeSession } = get();
        if (activatedSessionId && !sessionStores[activatedSessionId]) {
          activeSession(activatedSessionId);
        }
      }
    },
    createSingleSession: async (userId) => {
      const { client, insertSession } = get();
      if (client) {
        const session = await client.Api.session.createSingleChat(userId);
        const formattedSession = client.formatSession(session as unknown as OriginalSession);
        insertSession(formattedSession);
        return formattedSession;
      }
    },
    createGroupSession: async (
      group_name,
      avatar,
      description,
      invite_member_list,
      group_id_list
    ) => {
      const { client, insertSession } = get();
      if (client) {
        const result = await client.Api.session.createGroupChat(
          group_name,
          avatar,
          description,
          invite_member_list,
          group_id_list
        );
        const formattedSession = client.formatSession(result.session);
        insertSession(formattedSession);
        return formattedSession;
      }
    },
    joinSession: async (chat_id_like, join_mode) => {
      const { client, insertSession } = get();
      if (client) {
        const res = await client.Api.session.joinSession(
          chat_id_like,
          join_mode ?? GroupMemberJoinMode.Search
        );
        if (res.session) {
          insertSession(res.session);
        }
        return res;
      }
    },
    initSessions: async () => {
      const { client } = get();
      if (client) {
        set({ sessionsStatus: SessionsStatus.LOADING });
        client.Api.session.updateSessions().finally(() => {
          set({ sessionsStatus: SessionsStatus.READY });
        });
      }
    },

    activeSession: (session_id) => {
      console.log(`1. [halo-chat] activeSession. 
session_id: ${session_id}`);
      const {
        user,
        client,
        sessionStores,
        messagesMaxLimit,
        messagesLoadLimit,
        activatedSessionId,
        mediaActive,
        getSession,
        destroySession,
      } = get();

      if (!client) {
        return;
      }

      const session = getSession(session_id);

      if (session) {
        const activatedSession = sessionStores[activatedSessionId];

        if (activatedSession) {
          activatedSession.getState().setActivated(false);
          Object.assign(activatedSession, {
            __destroySessionTimer: setTimeout(
              () => {
                destroySession(activatedSessionId);
              },
              5 * 60 * 1000
            ),
          });
          console.log(
            `2. [halo-chat] setTimeout. 
session_id: ${session_id}
activatedSessionId: ${activatedSessionId}
__destroySessionTimer: ${(activatedSession as any).__destroySessionTimer}`
          );
        }

        let sessionStore = sessionStores[session_id];

        if (!sessionStore) {
          console.log(`3. [halo-chat] createSessionStore.
session_id: ${session_id}`);
          sessionStore = createSessionStore({
            user,
            client,
            session,
            activated: false,
            maxLimit: messagesMaxLimit,
            loadLimit: messagesLoadLimit,
          });

          set((store) => {
            store.sessionStores[session_id] = sessionStore;
          });
        }

        sessionStore.getState().setActivated(true);
        // @ts-expect-error ignore
        clearTimeout(sessionStore.__destroySessionTimer);
        console.log(`5. [halo-chat] clearTimeout.
session_id: ${session_id}
__destroySessionTimer: ${(sessionStore as any).__destroySessionTimer}`);

        mediaActive(session_id);
      }

      client.Api.session.setSessionReadMark(session_id, false);

      set({ activatedSessionId: session_id });
    },

    destroySession: (session_id) => {
      set((store) => {
        const sessionStore = store.sessionStores[session_id];
        if (sessionStore) {
          sessionStore.getState().destroySession();
          delete store.sessionStores[session_id];
          console.log(`7. [halo-chat] destroySessions.
session_id: ${session_id}`);
        }
      });
    },

    removeSession: (session_id) => {
      const { destroySession } = get();

      set((store) => {
        const index = store.sessions.findIndex((s) => s.chat_id === session_id);
        if (index !== -1) {
          store.sessions.splice(index, 1);
        }
        const indexCollapsed = store.sessionsCollapsed.findIndex((s) => s.chat_id === session_id);
        if (indexCollapsed !== -1) {
          store.sessionsCollapsed.splice(indexCollapsed, 1);
        }

        if (store.activatedSessionId === session_id) {
          store.activatedSessionId = undefined;
        }
      });

      destroySession(session_id);
    },

    insertSession: (session) => {
      set((store) => {
        const sessions = store.sessions;
        const index = sessions.findIndex((s) => s.chat_id === session.chat_id);
        if (index !== -1) {
          sessions[index] = session;
        } else {
          sessions.push(session);
        }
        sessions.sort((a, b) => b.update_at - a.update_at);
      });
    },
    updateSession: (session) => {
      let session_: Session;

      set((store) => {
        const sessions = store.sessions;
        const index = sessions.findIndex((s) => s.chat_id === session.chat_id);
        if (index !== -1) {
          session_ = deepMerge(sessions[index], session) as Session;
          sessions[index] = session_;
          if (session.last_message) {
            sessions.sort((a, b) => b.update_at - a.update_at);
          }
        }
      });

      if (session_) {
        const { sessionStores } = get();
        const sessionStore = sessionStores[session_.chat_id];
        if (sessionStore) {
          sessionStore.getState().setSession(session_);
        }
      }
    },
    getSession: (session_id) => {
      return get().sessions.find((s) => s.chat_id === session_id);
    },
    hideSession: async (session_id, is_delete) => {
      const { client, removeSession } = get();
      if (client) {
        await client.Api.session.hideSessions([session_id], is_delete ?? false);
        client.dispatchEvent('HideSessions', {
          chat_ids: [session_id],
          is_delete: is_delete ?? false,
        });
        await doExpireAnimate();
        removeSession(session_id);
      }
    },
    setSessionCollapse: async (chat_id, collapse_group_id) => {
      const { client } = get();
      if (client) {
        await client.Api.session.setSessionCollapse(chat_id, collapse_group_id);
      }
    },
    setSessionMuted: async (chat_id, muted) => {
      const { client } = get();
      if (client) {
        await client.Api.session.setSessionMuted(chat_id, muted);
      }
    },
    setSessionAtAllMuted: async (chat_id, muted) => {
      const { client } = get();
      if (client) {
        await client.Api.session.setSessionAtAllMuted(chat_id, muted);
      }
    },
    setPinnedSessions(chat_ids) {
      set({ pinnedSessions: chat_ids });
    },
    changePinnedList: async (chat_id, is_pinned, isCollapsed) => {
      const { client } = get();
      try {
        if (is_pinned && isCollapsed) {
          await client?.Api.session.setSessionCollapse(chat_id, '');
        }
        await client?.Api.session.setSessionPinned(chat_id, is_pinned).then(() => {
          const { pinnedSessions } = get();

          set({
            pinnedSessions: uniq(
              is_pinned
                ? [...pinnedSessions, chat_id]
                : pinnedSessions.filter((id) => id !== chat_id)
            ),
          });
        });

        return Promise.resolve();
      } catch (e) {
        return Promise.reject(
          safeJsonParse(safeJsonParse(e.message, { error: '' }).error, {
            message: 'Network error',
          }).message
        );
      }
    },
    getChatStatus: async (options) => {
      const { client, updateSession } = get();
      client?.Api.session.getChatStatus({ user_ids: Object.keys(options) }).then((res) => {
        if (res.data.media_call?.length) {
          res.data.media_call.forEach((user_id) => {
            const chat_id = options[user_id];
            if (chat_id) {
              updateSession({
                chat_id,
                isOnACall: true,
              });
            }
          });
        }
      });
    },
  }) as AppStore;
